/**
 * 本地通知状态管理
 * 职责：
 * - 读取/写入 latestSeen（最后查看的通知）
 * - 兼容读取 legacy read ids（旧的已读通知列表）
 * - 按用户隔离存储
 * - 清理超 30 天的已读记录
 * - 提供纯函数判定（是否显示红点、是否已读）
 */

import log from "logbeacon";
import type { NotificationListItem } from "./mockNotifications";
import {
  upsertLatestSeenToSupabase,
  upsertNotifyActionHiddenToSupabase,
  upsertNotifyMessageReadToSupabase,
} from "@/src/features/notification/sync/supabaseNotificationRepository";
import { emitNotificationUserStateUpdated } from "@/src/features/notification/sync/notificationSyncSignal";
import { isNotificationSyncDebugEnabled } from "@/src/features/notification/sync/notificationSyncTelemetry";

// Local Storage Keys
const STORAGE_KEY_LATEST_SEEN = "notification_latest_seen_v1";
const STORAGE_KEY_READ_IDS = "notification_read_ids_v1";
const STORAGE_KEY_NOTIFY_MESSAGE_READ_MAP = "notification_message_read_map_v1";
const STORAGE_KEY_NOTIFY_ACTION_VISIBILITY =
  "notification_action_visibility_v1";

const EXPIRY_DAYS = 30;
const EXPIRY_MS = EXPIRY_DAYS * 24 * 60 * 60 * 1000;

/**
 * 最后查看的通知记录
 */
interface LatestSeenRecord {
  [userId: string]: { id: string; time: number };
}

/**
 * 已读通知 ID 列表
 */
interface LegacyReadIdsRecord {
  [userId: string]: string[];
}

type NotifyMessageReadMapStore = Record<string, Record<string, number>>;
type NotifyActionVisibilityStore = Record<
  string,
  Record<string, Record<string, number>>
>;
function canUseLocalStorage(): boolean {
  return typeof window !== "undefined" && typeof localStorage !== "undefined";
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function normalizeOwnerReadRecord(record: unknown): Record<string, number> {
  if (!isPlainObject(record)) {
    return {};
  }

  return Object.entries(record).reduce<Record<string, number>>(
    (acc, [messageId, time]) => {
      if (
        messageId.length > 0 &&
        typeof time === "number" &&
        Number.isFinite(time)
      ) {
        acc[messageId] = time;
      }

      return acc;
    },
    {},
  );
}

function normalizeNotifyMessageReadMapStore(
  store: unknown,
): NotifyMessageReadMapStore {
  if (!isPlainObject(store)) {
    return {};
  }

  return Object.fromEntries(
    Object.entries(store).map(([ownerId, record]) => [
      ownerId,
      normalizeOwnerReadRecord(record),
    ]),
  );
}

function readNotifyMessageReadMapStore(): NotifyMessageReadMapStore {
  if (!canUseLocalStorage()) {
    return {};
  }

  try {
    const data = localStorage.getItem(STORAGE_KEY_NOTIFY_MESSAGE_READ_MAP);
    if (!data) {
      return {};
    }

    const parsed = JSON.parse(data);
    return normalizeNotifyMessageReadMapStore(parsed);
  } catch (error) {
    log.error("[notification] Failed to read notify message read map:", error);
    return {};
  }
}

function normalizeOwnerActionVisibilityRecord(
  record: unknown,
): Record<string, Record<string, number>> {
  if (!isPlainObject(record)) {
    return {};
  }

  return Object.fromEntries(
    Object.entries(record).map(([messageId, catRecord]) => [
      messageId,
      normalizeOwnerReadRecord(catRecord),
    ]),
  );
}

function normalizeNotifyActionVisibilityStore(
  store: unknown,
): NotifyActionVisibilityStore {
  if (!isPlainObject(store)) {
    return {};
  }

  return Object.fromEntries(
    Object.entries(store).map(([ownerId, record]) => [
      ownerId,
      normalizeOwnerActionVisibilityRecord(record),
    ]),
  );
}

function readNotifyActionVisibilityStore(): NotifyActionVisibilityStore {
  if (!canUseLocalStorage()) {
    return {};
  }

  try {
    const data = localStorage.getItem(STORAGE_KEY_NOTIFY_ACTION_VISIBILITY);
    if (!data) {
      return {};
    }

    const parsed = JSON.parse(data);
    return normalizeNotifyActionVisibilityStore(parsed);
  } catch (error) {
    log.error(
      "[notification] Failed to read notify action visibility map:",
      error,
    );
    return {};
  }
}

function writeNotifyActionVisibilityStore(
  store: NotifyActionVisibilityStore,
): void {
  if (!canUseLocalStorage()) {
    return;
  }

  try {
    if (Object.keys(store).length === 0) {
      localStorage.removeItem(STORAGE_KEY_NOTIFY_ACTION_VISIBILITY);
      return;
    }

    localStorage.setItem(
      STORAGE_KEY_NOTIFY_ACTION_VISIBILITY,
      JSON.stringify(store),
    );
  } catch (error) {
    log.error(
      "[notification] Failed to write notify action visibility map:",
      error,
    );
  }
}

function writeNotifyMessageReadMapStore(
  store: NotifyMessageReadMapStore,
): void {
  if (!canUseLocalStorage()) {
    return;
  }

  try {
    if (Object.keys(store).length === 0) {
      localStorage.removeItem(STORAGE_KEY_NOTIFY_MESSAGE_READ_MAP);
      return;
    }

    localStorage.setItem(
      STORAGE_KEY_NOTIFY_MESSAGE_READ_MAP,
      JSON.stringify(store),
    );
  } catch (error) {
    log.error("[notification] Failed to write notify message read map:", error);
  }
}

function getReadIdsFromMessageReadMap(userId: string): string[] {
  const readMap = getNotifyMessageReadMap(userId);
  if (readMap.size === 0) {
    return [];
  }

  return Array.from(readMap.keys());
}

export function getNotifyMessageReadMap(ownerId?: string): Map<string, number> {
  if (!ownerId) {
    return new Map();
  }

  const store = readNotifyMessageReadMapStore();
  const ownerRecord = store[ownerId];

  if (!ownerRecord || Object.keys(ownerRecord).length === 0) {
    return new Map();
  }

  return new Map(Object.entries(ownerRecord));
}

export function markNotifyMessageAsRead(
  ownerId: string | undefined,
  messageId: string | undefined,
  time: number | undefined,
  options?: { syncToSupabase?: boolean },
): void {
  if (!ownerId || !messageId || typeof time !== "number") {
    return;
  }

  const store = readNotifyMessageReadMapStore();
  const ownerRecord = store[ownerId] ?? {};
  store[ownerId] = {
    ...ownerRecord,
    [messageId]: time,
  };
  writeNotifyMessageReadMapStore(store);
  if (options?.syncToSupabase !== false) {
    void upsertNotifyMessageReadToSupabase(ownerId, messageId, time);
  }
  emitNotificationUserStateUpdated();
}

export function isNotifyMessageRead(
  ownerId: string | undefined,
  messageId: string | undefined,
  createdAt: number | undefined,
  now = Date.now(),
): boolean {
  if (typeof createdAt === "number" && now - createdAt > EXPIRY_MS) {
    return true;
  }

  if (!ownerId || !messageId) {
    return false;
  }

  return getNotifyMessageReadMap(ownerId).has(messageId);
}

export function cleanupExpiredNotifyMessageReadMap(
  now = Date.now(),
  ownerId?: string,
): void {
  const store = readNotifyMessageReadMapStore();
  if (ownerId) {
    const ownerRecord = store[ownerId];
    if (!ownerRecord) {
      return;
    }

    const nextOwnerRecord = Object.fromEntries(
      Object.entries(ownerRecord).filter(([, time]) => now - time <= EXPIRY_MS),
    );

    if (JSON.stringify(nextOwnerRecord) === JSON.stringify(ownerRecord)) {
      return;
    }

    if (Object.keys(nextOwnerRecord).length === 0) {
      delete store[ownerId];
    } else {
      store[ownerId] = nextOwnerRecord;
    }
    writeNotifyMessageReadMapStore(store);
    return;
  }

  const nextStore = Object.fromEntries(
    Object.entries(store)
      .map(([ownerId, record]) => [
        ownerId,
        Object.fromEntries(
          Object.entries(record).filter(([, time]) => now - time <= EXPIRY_MS),
        ),
      ])
      .filter(([, record]) => Object.keys(record).length > 0),
  );

  if (JSON.stringify(nextStore) !== JSON.stringify(store)) {
    writeNotifyMessageReadMapStore(nextStore);
  }
}

export function getNotifyActionVisibilityMap(
  ownerId?: string,
  messageId?: string,
): Map<string, number> {
  if (!ownerId || !messageId) {
    return new Map();
  }

  const store = readNotifyActionVisibilityStore();
  const messageRecord = store[ownerId]?.[messageId];
  if (!messageRecord || Object.keys(messageRecord).length === 0) {
    return new Map();
  }

  return new Map(Object.entries(messageRecord));
}

export function hideNotifyAction(
  ownerId?: string,
  messageId?: string,
  catId?: string,
  hiddenAt = Date.now(),
  options?: { syncToSupabase?: boolean },
): void {
  if (
    !ownerId ||
    !messageId ||
    !catId ||
    typeof hiddenAt !== "number" ||
    !Number.isFinite(hiddenAt)
  ) {
    return;
  }

  const store = readNotifyActionVisibilityStore();
  const ownerRecord = store[ownerId] ?? {};
  const messageRecord = ownerRecord[messageId] ?? {};
  store[ownerId] = {
    ...ownerRecord,
    [messageId]: {
      ...messageRecord,
      [catId]: hiddenAt,
    },
  };
  writeNotifyActionVisibilityStore(store);
  if (isNotificationSyncDebugEnabled()) {
    log.info("[sync-debug][local-hide-write]", {
      ownerId,
      messageId,
      catId,
      hiddenAt,
      syncToSupabase: options?.syncToSupabase !== false,
    });
    log.info("[sync-debug][local-hide-write]", {
      ownerId,
      messageId,
      catId,
      hiddenAt,
      syncToSupabase: options?.syncToSupabase !== false,
    });
  }
  if (options?.syncToSupabase !== false) {
    void upsertNotifyActionHiddenToSupabase(
      ownerId,
      messageId,
      catId,
      hiddenAt,
    );
  }
  emitNotificationUserStateUpdated();
}

export function hideNotifyActions(
  ownerId?: string,
  messageId?: string,
  catIds?: string[],
  hiddenAt = Date.now(),
  options?: { syncToSupabase?: boolean },
): void {
  if (!Array.isArray(catIds) || catIds.length === 0) {
    return;
  }

  const validCatIds = Array.from(
    new Set(
      catIds.filter((catId) => typeof catId === "string" && catId.length > 0),
    ),
  );

  validCatIds.forEach((catId) => {
    hideNotifyAction(ownerId, messageId, catId, hiddenAt, options);
  });
}

export function isNotifyActionHidden(
  ownerId?: string,
  messageId?: string,
  catId?: string,
  now = Date.now(),
): boolean {
  if (!ownerId || !messageId || !catId) {
    return false;
  }

  const hiddenAt = getNotifyActionVisibilityMap(ownerId, messageId).get(catId);
  if (isNotificationSyncDebugEnabled()) {
    log.info("[sync-debug][local-hide-read]", {
      ownerId,
      messageId,
      catId,
      hiddenAt,
      now,
      isHidden:
        typeof hiddenAt === "number" ? now - hiddenAt <= EXPIRY_MS : false,
    });
    log.info("[sync-debug][local-hide-read]", {
      ownerId,
      messageId,
      catId,
      hiddenAt,
      now,
      isHidden:
        typeof hiddenAt === "number" ? now - hiddenAt <= EXPIRY_MS : false,
    });
  }
  if (typeof hiddenAt !== "number") {
    return false;
  }

  return now - hiddenAt <= EXPIRY_MS;
}

export function cleanupExpiredNotifyActionVisibility(
  now = Date.now(),
  ownerId?: string,
): void {
  const store = readNotifyActionVisibilityStore();
  if (ownerId) {
    const ownerRecord = store[ownerId];
    if (!ownerRecord) {
      return;
    }

    const nextOwnerRecord = Object.fromEntries(
      Object.entries(ownerRecord)
        .map(([messageId, catRecord]) => [
          messageId,
          Object.fromEntries(
            Object.entries(catRecord).filter(
              ([, hiddenAt]) => now - hiddenAt <= EXPIRY_MS,
            ),
          ),
        ])
        .filter(([, catRecord]) => Object.keys(catRecord).length > 0),
    );

    if (JSON.stringify(nextOwnerRecord) === JSON.stringify(ownerRecord)) {
      return;
    }

    if (Object.keys(nextOwnerRecord).length === 0) {
      delete store[ownerId];
    } else {
      store[ownerId] = nextOwnerRecord;
    }
    writeNotifyActionVisibilityStore(store);
    return;
  }

  const nextStore = Object.fromEntries(
    Object.entries(store)
      .map(([ownerId, messageRecord]) => [
        ownerId,
        Object.fromEntries(
          Object.entries(messageRecord)
            .map(([messageId, catRecord]) => [
              messageId,
              Object.fromEntries(
                Object.entries(catRecord).filter(
                  ([, hiddenAt]) => now - hiddenAt <= EXPIRY_MS,
                ),
              ),
            ])
            .filter(([, catRecord]) => Object.keys(catRecord).length > 0),
        ),
      ])
      .filter(([, messageRecord]) => Object.keys(messageRecord).length > 0),
  );

  if (JSON.stringify(nextStore) !== JSON.stringify(store)) {
    writeNotifyActionVisibilityStore(nextStore);
  }
}

/**
 * 从 localStorage 读取最后查看的通知
 * @param userId 用户 ID（可选，未登录时为 undefined）
 * @returns 最后查看的通知 { id, time } 或 null
 */
export function getLatestSeen(
  userId?: string,
): { id: string; time: number } | null {
  if (!userId) {
    return null;
  }

  try {
    const data = localStorage.getItem(STORAGE_KEY_LATEST_SEEN);
    if (!data) {
      return null;
    }

    const record: LatestSeenRecord = JSON.parse(data);
    return record[userId] || null;
  } catch (error) {
    log.error("[notification] Failed to read latestSeen:", error);
    return null;
  }
}

/**
 * 写入最后查看的通知
 * @param userId 用户 ID
 * @param value { id, time }
 */
export function setLatestSeen(
  userId: string | undefined,
  value: { id: string; time: number },
  options?: { syncToSupabase?: boolean },
): void {
  if (!userId) {
    return;
  }

  try {
    const data = localStorage.getItem(STORAGE_KEY_LATEST_SEEN);
    const record: LatestSeenRecord = data ? JSON.parse(data) : {};
    record[userId] = value;
    localStorage.setItem(STORAGE_KEY_LATEST_SEEN, JSON.stringify(record));
    if (options?.syncToSupabase !== false) {
      void upsertLatestSeenToSupabase(userId, value.id, value.time);
    }
    emitNotificationUserStateUpdated();
  } catch (error) {
    log.error("[notification] Failed to write latestSeen:", error);
  }
}

/**
 * 从 localStorage 读取 legacy 已读通知 ID 列表
 * @param userId 用户 ID（可选，未登录时为 undefined）
 * @returns 已读通知 ID 数组
 */
export function getReadIds(userId?: string): string[] {
  if (!userId) {
    return [];
  }

  const derivedReadIds = getReadIdsFromMessageReadMap(userId);
  if (derivedReadIds.length > 0) {
    return derivedReadIds;
  }

  try {
    const data = localStorage.getItem(STORAGE_KEY_READ_IDS);
    if (!data) {
      return [];
    }

    const record: LegacyReadIdsRecord = JSON.parse(data);
    return record[userId] || [];
  } catch (error) {
    log.error("[notification] Failed to read legacy read ids:", error);
    return [];
  }
}

/**
 * 标记一条通知为已读。主写路径走 message_read_map。
 * @param userId 用户 ID
 * @param id 通知 ID
 */
export function markAsRead(userId: string | undefined, id: string): void {
  if (!userId) {
    return;
  }

  try {
    void markNotifyMessageAsRead(userId, id, Date.now());
  } catch (error) {
    log.error("[notification] Failed to mark as read:", error);
  }
}

/**
 * 兼容清理 legacy read ids。
 * 新主链路走 message_read_map，这里只负责历史数据兜底读取。
 * @param userId 用户 ID
 * @param list 当前通知列表
 */
export function cleanupExpiredReadIds(
  userId: string | undefined,
  list: NotificationListItem[],
): string[] {
  if (!userId || list.length === 0) {
    return getReadIds(userId);
  }

  try {
    const derivedReadIds = getReadIdsFromMessageReadMap(userId);
    if (derivedReadIds.length > 0) {
      return derivedReadIds;
    }

    return getReadIds(userId);
  } catch (error) {
    log.error("[notification] Failed to cleanup legacy read ids:", error);
    return getReadIds(userId);
  }
}

/** Server list head for bell red dot (`id` + `created` as `time`). */
export type NotificationBellHead = { id: string; time: number };

/**
 * Whether to show the chat bell red dot from server head vs local watermark.
 * @param latestHead First valid row on API page 1 (same filter as notify list)
 * @param latestSeen Local last-seen record
 */
export function shouldShowNotificationDot(
  latestHead: NotificationBellHead | null,
  latestSeen: NotificationBellHead | null,
): boolean {
  if (!latestHead) {
    return false;
  }
  if (!latestSeen) {
    return true; // No local record: show dot when there is a valid head
  }

  return latestHead.id !== latestSeen.id && latestHead.time > latestSeen.time;
}

/**
 * 判断一条通知是否已读
 * 已读条件：
 * 1. 命中 30 天规则（距离锚点超过 30 天），或
 * 2. 在已读列表中
 * @param item 通知项目
 * @param anchor 锚点（通常为列表第一条）
 * @param legacyReadIds legacy 已读 ID 列表
 * @returns true 已读，false 未读
 */
export function isNotificationRead(
  item: NotificationListItem,
  anchor: NotificationListItem,
  legacyReadIds: string[],
): boolean {
  // 规则 1：距离锚点超过 30 天，自动已读
  if (anchor.time - item.time > EXPIRY_MS) {
    return true;
  }

  // 规则 2：在已读列表中
  return legacyReadIds.includes(item.id);
}

/**
 * 清空某个用户的铃铛通知数据（登出时调用）
 * @param userId 用户 ID
 */
export function clearNotificationData(userId: string | undefined): void {
  if (!userId) {
    return;
  }

  try {
    // 清理 latestSeen
    const latestSeenData = localStorage.getItem(STORAGE_KEY_LATEST_SEEN);
    if (latestSeenData) {
      const record: LatestSeenRecord = JSON.parse(latestSeenData);
      delete record[userId];
      if (Object.keys(record).length === 0) {
        localStorage.removeItem(STORAGE_KEY_LATEST_SEEN);
      } else {
        localStorage.setItem(STORAGE_KEY_LATEST_SEEN, JSON.stringify(record));
      }
    }

    // 清理 legacy read ids，避免旧缓存长期残留
    const legacyReadIdsData = localStorage.getItem(STORAGE_KEY_READ_IDS);
    if (legacyReadIdsData) {
      const record: LegacyReadIdsRecord = JSON.parse(legacyReadIdsData);
      delete record[userId];
      if (Object.keys(record).length === 0) {
        localStorage.removeItem(STORAGE_KEY_READ_IDS);
      } else {
        localStorage.setItem(STORAGE_KEY_READ_IDS, JSON.stringify(record));
      }
    }
  } catch (error) {
    log.error("[notification] Failed to clear notification data:", error);
  }
}
